import { useRef } from "react";

import { useChatClass } from "../../theme";
import { Avatar } from "../Avatar";
import { Card } from "../Card";
import { Carousel } from "../Carousel";
import { Image } from "../Image";
import { Text } from "../Text";
import { Timestamp } from "../Timestamp";
import { MessageType } from "./constants";
import { Content, SenderName, SystemMessageContainer } from "./styled";
import {
  CardMessageProps,
  CarouselMessageProps,
  ImageMessageProps,
  SystemMessageProps,
  TextMessageProps,
} from "./types";

/** An individual message within a system response */
export const SystemMessage: React.FC<
  React.PropsWithChildren<{
    /** An image URL for an avatar to associate this message with. */
    avatar: string;
    /** A unix timestamp indicating when this message was sent. */
    timestamp: number;
    /** Optional preformatted timestamp text. */
    timestampLabel?: string;
    /** Optional human sender name shown beside the timestamp. */
    senderName?: string;
    /** A single message to render with a {@link Message} component. */
    message: SystemMessageProps;
    /** If true, renders an avatar next to the message. */
    withImage: boolean;
    /** Custom styles to apply to the message container. */
    containerStyle?: React.CSSProperties;
    /** Optional callback when a link in text messages is clicked */
    onLinkClick?: (href: string) => void;
  }>
> = ({
  avatar,
  timestamp,
  timestampLabel,
  senderName,
  message,
  withImage,
  containerStyle,
  onLinkClick,
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const chatClass = useChatClass();

  return (
    <div
      style={
        message.type === MessageType.CAROUSEL
          ? { position: "relative" } // Required for scrollable carousels
          : undefined
      }
    >
      <SystemMessageContainer
        ref={containerRef}
        $withImage={withImage}
        $scrollable={message.type === MessageType.CAROUSEL}
        style={containerStyle}
        className={`${chatClass("system-message")} ${chatClass(
          "system-message",
          message.type,
        )}`}
      >
        <Avatar size="small" avatar={avatar} />
        <Content className={chatClass("system-message-content")}>
          {message.type === MessageType.TEXT && (
            <Text
              text={(message as TextMessageProps).text}
              onLinkClick={onLinkClick}
            />
          )}
          {message.type === MessageType.IMAGE && (
            <Image image={(message as ImageMessageProps).url} />
          )}
          {message.type === MessageType.CARD && (
            <Card
              description={(message as CardMessageProps).description}
              title={(message as CardMessageProps).title}
              actions={(message as CardMessageProps).actions}
              image={(message as CardMessageProps).image}
            />
          )}
          {message.type === MessageType.CAROUSEL && (
            <Carousel
              cards={(message as CarouselMessageProps).cards}
              scrollContainerRef={containerRef}
            />
          )}
        </Content>
        <Timestamp value={timestamp} label={timestampLabel} />
      </SystemMessageContainer>
      {senderName ? <SenderName>{senderName}</SenderName> : null}
    </div>
  );
};
